import React, { useMemo, useRef } from "react";

import { Box } from "@mui/material";
import { AsyncContentMui } from "components";
import HighchartsReact from "highcharts-react-official";
import { useDevice } from "hooks";
import { CashflowGraph, ReportCashflowAum } from "store/types";

import { CashflowChartComponent } from "./CashflowChartComponent";
import { EmptyCashflow } from "./EmptyCashflow";

interface CashflowChartProps {
  data: CashflowGraph | undefined;
  aumData: ReportCashflowAum | null;
  loading: boolean;
}

export const CashflowChart: React.FC<CashflowChartProps> = ({ data, aumData, loading }) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const { isMobile } = useDevice();

  const hasData = useMemo(() => {
    if (!data || data.length === 0) return false;
    // Check if all values in all arrays are zero
    return data.some((arr) => arr.some((val) => val !== 0));
  }, [data]);

  if (!hasData && !loading) {
    return <EmptyCashflow />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: "0px",
        backgroundColor: "#ffffff",
        height: "350px",
        maxHeight: "350px",
        width: "100%",
        border: "none",
        ...(isMobile
          ? {
              overflowX: "auto",
              overflowY: "hidden",
              "&::-webkit-scrollbar": {
                display: "block",
                height: "4px"
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1"
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "2px"
              }
            }
          : {
              overflow: "hidden",
              "&::-webkit-scrollbar": {
                display: "none"
              },
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "& .highcharts-container": {
                width: "100% !important"
              }
            })
      }}
    >
      <AsyncContentMui isLoading={loading} spinnerProps={{ size: 50 }}>
        <CashflowChartComponent data={data} chartRef={chartRef} aumData={aumData} />
      </AsyncContentMui>
    </Box>
  );
};
